/**
 * Login page — кнопка Telegram Login Widget.
 *
 * Виджет встраивается через <script async src="https://telegram.org/js/telegram-widget.js?22"
 *   data-telegram-login="bemliBot" data-onauth="onTelegramAuth(user)" data-size="large">
 *
 * При успехе TG вызывает window.onTelegramAuth(payload). Мы перехватываем
 * этот колбэк и шлём в Refine login(), который дальше идёт в наш authProvider.
 */
import { useEffect, useRef } from "react";
import { useLogin } from "@refinedev/core";
import { Card, Typography, Alert, Space } from "antd";
import type { TgLoginPayload } from "../authProvider";

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME || "bemliBot";

declare global {
  interface Window {
    onTelegramAuth?: (user: TgLoginPayload) => void;
  }
}

export const LoginPage = () => {
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const { mutate: login, isLoading, error } = useLogin<TgLoginPayload>();

  useEffect(() => {
    if (!widgetContainerRef.current) return;
    widgetContainerRef.current.innerHTML = "";
    // eslint-disable-next-line no-console
    console.log("[bemli-login] mounting TG widget bot=", BOT_USERNAME);

    window.onTelegramAuth = (user) => {
      // eslint-disable-next-line no-console
      console.log("[bemli-login] TG callback received:", user);
      login(user, {
        onSuccess: (r) => console.log("[bemli-login] login.onSuccess:", r),
        onError:   (e) => console.error("[bemli-login] login.onError:", e),
      });
    };

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", BOT_USERNAME);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-userpic", "false");
    script.setAttribute("data-radius", "8");
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    script.setAttribute("data-request-access", "write");
    script.onload  = () => console.log("[bemli-login] TG widget script loaded");
    script.onerror = (e) => console.error("[bemli-login] TG widget script FAILED to load:", e);
    widgetContainerRef.current.appendChild(script);

    return () => {
      window.onTelegramAuth = undefined;
    };
  }, [login]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0d1117",
        padding: 24,
      }}
    >
      <Card
        style={{
          maxWidth: 420, width: "100%",
          boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
        }}
        styles={{ body: { padding: 32 } }}
      >
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              Bemli — admin
            </Typography.Title>
            <Typography.Text type="secondary">
              Вход через Telegram
            </Typography.Text>
          </div>

          {error && (
            <Alert
              type="error"
              showIcon
              message="Не удалось войти"
              description={String((error as any)?.message || error)}
            />
          )}
          {isLoading && (
            <Alert type="info" showIcon message="Проверяем учётные данные…" />
          )}

          <div
            ref={widgetContainerRef}
            style={{ display: "flex", justifyContent: "center", minHeight: 60 }}
          />

          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, marginBottom: 0, textAlign: "center" }}
          >
            Доступ только для аккаунтов, добавленных в
            <code> admin_users</code> на сервере. Если кнопка ниже не появилась —
            проверь, что <code>admin.bemli.org</code> прописан в BotFather как
            домен Login Widget.
          </Typography.Paragraph>
        </Space>
      </Card>
    </div>
  );
};
